import type { AccountType } from '@safc/api-client';
import {
  QueryUnitProcessState,
  unitProcessControllerGetUnitProcesses,
} from '@safc/api-client';
import { get } from 'es-toolkit/compat';
import { has } from 'es-toolkit/compat';
import type {
  HoldingsProcess,
  ProcessResponse,
} from 'features/units/api/process/process.types.js';
import { mapProcess } from 'features/units/api/process/process.types.js';
import {
  parseAsString,
  useQueryState,
} from 'nuqs';
import type {
  Dispatch,
  SetStateAction,
} from 'react';
import {
  useEffect,
  useState,
} from 'react';

type Context = {
  isRegistryAdmin: boolean;
  ownerId: string;
  currentUserId: string;
};

export const badgeNeededCheckers: Record<
  string,
  (context: Context) => Promise<boolean>
> = {
  pendingActions: async (context) => {
    if (!context.ownerId) {
      return hasPendingActions([], context);
    }

    const { processes } = await unitProcessControllerGetUnitProcesses({
      state: QueryUnitProcessState.pending,
      accountId: context.ownerId,
    });

    const mappedProcesses = (
      (processes as ProcessResponse['processes']) ?? []
    ).map(process => mapProcess(process, context.ownerId));
    return hasPendingActions(mappedProcesses, context);
  },
};

const hasPendingActions = (
  processes: HoldingsProcess[],
  { ownerId, isRegistryAdmin, currentUserId }: Context,
) => {
  return processes.some((process) => {
    if (process.state === 'pendingAcceptance') {
      if (
        ownerId === process.sendingAccountId
        || isRegistryAdmin
        || (has(process?.metadata, ['safcClaimBeneficiary', 'ownerId'])
          && get(process?.metadata, ['safcClaimBeneficiary', 'ownerId'])
          !== ownerId)
      ) {
        return false;
      }
    }
    if (process.state === 'pendingApproval') {
      if (!isRegistryAdmin) {
        return false;
      }
    }
    if (process.state === 'pendingSignatures') {
      if (isRegistryAdmin) {
        return false;
      }
      if (process.signatures!.some(s => s.userId === currentUserId)) {
        return false;
      }
    }
    return true;
  });
};

function useTabState(
  defaultValue?: string | number,
): [string | number | null, Dispatch<SetStateAction<string | number | null>>] {
  return useState<string | number | null>(defaultValue ?? null);
}

function useTabQueryParam(
  searchParam: string,
  defaultValue?: string | number,
): [string | number | null, (value: string | null) => void] {
  return useQueryState(
    searchParam,
    parseAsString.withDefault(`${defaultValue}`),
  );
}

export function useConditionalQueryParam({
  searchParam,
  defaultValue,
}: {
  searchParam?: string;
  defaultValue?: string | number;
}): [string | number | null, (value: string | null) => void] {
  const [localTab, setLocalTab] = useTabState(defaultValue);
  const [queryTab, setQueryTab] = useTabQueryParam(
    searchParam ?? '',
    defaultValue,
  );

  // Clear query parameter when component unmounts
  useEffect(() => {
    return () => {
      if (searchParam) {
        setQueryTab(null);
      }
    };
  }, [searchParam, setQueryTab]);

  return searchParam ? [queryTab, setQueryTab] : [localTab, setLocalTab];
}

export const renderCheckers = {
  byAccountType: (type: AccountType | undefined) => {
    return type === 'fuelProviderHoldingAccount';
  },
};
